import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  PhoneReport,
  PhoneReportByTestIdInfo,
  PhoneReportCollection,
  PhoneReportCreate,
  PhoneReportIssue,
  PhoneReportOutcomeUpdateRequest,
  PhoneReportsByTestsIdsRequestDto,
} from '../models/phone-report.model';
import { TestResult } from '../models/test-result.model';
import { RestUtils } from './rest-utils';
import { TestResultUtils } from './test-result-utils';

@Injectable()
export class PhoneReportService {
  private readonly http = inject(HttpClient);

  utils = new RestUtils();

  headers = new HttpHeaders();

  private actionSubject: Subject<PhoneReportActionData> = new Subject<PhoneReportActionData>();
  action$ = this.actionSubject.asObservable();

  issues: Record<PhoneReportIssue, string> = {
    [PhoneReportIssue.DOES_NOT_SHOW]: 'Does not show test results',
    [PhoneReportIssue.EXPIRED_SIM]: 'Expired SIM/incorrect number',
    [PhoneReportIssue.WHITELISTED]: 'Whitelisted',
    [PhoneReportIssue.PORTED]: 'Incorrect MCC/MNC',
  };

  constructor(http: HttpClient) {
    this.http = http;
    this.headers = this.headers.set('Content-Type', 'application/json');
  }

  announceAction(action: PhoneReportActionData) {
    this.actionSubject.next(action);
  }

  all(params: PhoneReportRequestParams) {
    const queryParams = {
      page: params.page,
      size: params.size,
      sort: params.sort.length ? params.sort : [],
      search: params.search ? params.search : '',
    };

    const url = this.utils.buildUrl(`ROLE/usr/phone-reports`, queryParams);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<PhoneReportCollection>(url, options).pipe(
      map(_ => {
        const data = _;
        data.content = data.content.map(_ => this.prepare(_));
        return data;
      })
    );
  }

  getTestResult(reportId: number): Observable<TestResult> {
    const url = this.utils.buildUrl(`ROLE/usr/phone-reports/${reportId}/test-result`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.get<TestResult>(url, options).pipe(map(r => TestResultUtils.prepareTestResult(r)));
  }

  getPhoneReportsInfoByTestIds(dto: PhoneReportsByTestsIdsRequestDto): Observable<PhoneReportByTestIdInfo[]> {
    const url = this.utils.buildUrl(`ROLE/usr/phone-reports/by-tests`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<PhoneReportByTestIdInfo[]>(url, dto, options);
  }

  create(report: PhoneReportCreate): Observable<HttpResponse<unknown>> {
    const url = this.utils.buildUrl(`ROLE/usr/phone-reports`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.post<unknown>(url, report, {
      ...options,
      observe: 'response',
    });
  }

  updateOutcomeResolution(reportId: number, dto: PhoneReportOutcomeUpdateRequest): Observable<HttpResponse<void>> {
    const url = this.utils.buildUrl(`ROLE/usr/phone-reports/${reportId}/outcome-resolution`);
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.put<void>(url, dto, {
      ...options,
      observe: 'response',
    });
  }

  delete(reportId: number) {
    const url = this.utils.buildUrl(`ROLE/usr/phone-reports/${reportId}`, {});
    const options = this.utils.getHttpHeaderOptions(this.headers);

    return this.http.delete(url, options);
  }

  prepare(report: PhoneReport) {
    report.createdAt = new Date(report.createdAt);

    if (report.testCreatedAt) {
      report.testCreatedAt = new Date(report.testCreatedAt);
    }

    if (Array.isArray(report.pings)) {
      report.pings = report.pings.map(ping => ({
        ...ping,
        timestamp: new Date(ping.timestamp),
      }));
    }

    return report;
  }

  createModel(): PhoneReportCreate {
    return {
      description: null,
      issue: 'DOES_NOT_SHOW',
      phone: null,
      testId: null,
      testType: null,
    };
  }
}

export class PhoneReportRequestParams {
  size = 20;
  page = 1;
  search: string;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}

export interface PhoneReportActionData {
  name: string;
  row: PhoneReport;
  column: string;
}
